import React, { useEffect, useMemo, useState } from 'react';
import { Alert, AppBar, Box, Button, CircularProgress, Dialog, FormControlLabel, IconButton, InputAdornment, List, ListItemButton, ListItemText, Switch, TextField, Toolbar, Typography, useMediaQuery, useTheme } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ClearIcon from '@mui/icons-material/Clear';
import { useAppContext } from '../../hooks/AppContext';
import { getTasksForSearch } from '../../services/GraphService/GraphService';
import { Slot } from '../slots/Slot';
import { TaskDetailView } from '../../onethreefive/components/TaskDetailView';
import dateTimeUtils from '../../utils/DateTimeUtils';
import Logger from '../../utils/Logger';
import { highlightParts, MAX_RESULTS, MIN_QUERY_LENGTH, queryWords, searchTasks } from './searchTasks';
const DEBOUNCE_MS = 250;
const listLabel = (listName) => listName === 'flaggedEmails' ? 'Flagged emails' : 'Tasks';
// TaskDetailView works from a slot; this one is only used to open the dialog and never goes on the board
const toSlot = (task) => {
    var _a, _b;
    return Slot.createSlot(0, task.listName, task.getId(), null, (_a = task.getTitle()) !== null && _a !== void 0 ? _a : null, task.getStatus(), (_b = task.getHeaderTitle()) !== null && _b !== void 0 ? _b : undefined, task.getCheckListItemId(), task.type, task.getWebLink());
};
const Highlighted = ({ text, words }) => (React.createElement(React.Fragment, null, highlightParts(text, words).map((part, index) => part.match ? React.createElement("strong", { key: index }, part.text) : React.createElement("span", { key: index }, part.text))));
const describe = (result) => {
    const { task, parent } = result;
    const parts = [];
    if (parent != undefined) {
        parts.push(`in ${parent.getTitle()}`);
    }
    else {
        parts.push(listLabel(task.listName));
    }
    const due = task.getDueDateTime();
    if (due != undefined && due != null) {
        parts.push(`due ${dateTimeUtils(due).format('M/D/Y')}`);
    }
    parts.push(task.getStatus() === 'completed' ? 'completed' : 'open');
    return parts.join(' · ');
};
export const SearchDialog = ({ open, onClose }) => {
    const app = useAppContext();
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down('md'));
    const [query, setQuery] = useState('');
    const [debouncedQuery, setDebouncedQuery] = useState('');
    const [includeCompleted, setIncludeCompleted] = useState(false);
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [failed, setFailed] = useState(false);
    const [reloads, setReloads] = useState(0);
    const [selected, setSelected] = useState(null);
    useEffect(() => {
        const id = setTimeout(() => setDebouncedQuery(query), DEBOUNCE_MS);
        return () => clearTimeout(id);
    }, [query]);
    useEffect(() => {
        if (!open || app.authProvider == undefined) {
            return;
        }
        let cancelled = false;
        setLoading(true);
        setFailed(false);
        getTasksForSearch(app.authProvider, includeCompleted)
            .then(found => { if (!cancelled)
            setItems(found); })
            .catch(error => {
            Logger.error(`Search: could not load tasks: ${error}`);
            if (!cancelled)
                setFailed(true);
        })
            .finally(() => { if (!cancelled)
            setLoading(false); });
        return () => { cancelled = true; };
    }, [open, includeCompleted, reloads, app.authProvider]);
    const words = useMemo(() => queryWords(debouncedQuery), [debouncedQuery]);
    const { results, total } = useMemo(() => searchTasks(items, debouncedQuery, includeCompleted), [items, debouncedQuery, includeCompleted]);
    const searching = debouncedQuery.trim().length >= MIN_QUERY_LENGTH;
    return (React.createElement(React.Fragment, null,
        React.createElement(Dialog, { fullScreen: fullScreen, fullWidth: true, maxWidth: "md", open: open, onClose: onClose },
            React.createElement(AppBar, { sx: { position: 'relative' } },
                React.createElement(Toolbar, null,
                    React.createElement(IconButton, { edge: "start", color: "inherit", "aria-label": "close search", onClick: onClose },
                        React.createElement(CloseIcon, null)),
                    React.createElement(Typography, { sx: { ml: 2 } }, "Search tasks"))),
            React.createElement(Box, { sx: { p: 2 } },
                React.createElement(TextField, { autoFocus: true, fullWidth: true, size: "small", placeholder: "Search tasks and subtasks", value: query, onChange: event => setQuery(event.target.value), InputProps: {
                        endAdornment: query.length > 0 && (React.createElement(InputAdornment, { position: "end" },
                            React.createElement(IconButton, { size: "small", "aria-label": "clear search", onClick: () => setQuery('') },
                                React.createElement(ClearIcon, null))))
                    } }),
                React.createElement(FormControlLabel, { control: React.createElement(Switch, { checked: includeCompleted, onChange: event => setIncludeCompleted(event.target.checked) }), label: "Include completed" })),
            loading && React.createElement(Box, { sx: { display: 'flex', justifyContent: 'center', p: 2 } },
                React.createElement(CircularProgress, null)),
            failed && (React.createElement(Alert, { severity: "error", sx: { m: 2 }, action: React.createElement(Button, { color: "inherit", size: "small", onClick: () => setReloads(reloads + 1) }, "Retry") }, "Couldn't load tasks")),
            !loading && !failed && searching && results.length === 0 && (React.createElement(Typography, { color: "text.secondary", sx: { p: 2 } }, "No tasks match")),
            React.createElement(List, null, results.map(result => {
                var _a;
                return (React.createElement(ListItemButton, { key: `${result.task.getId().taskId}/${result.task.getCheckListItemId()}`, onClick: () => setSelected(result.task) },
                    React.createElement(ListItemText, { primary: React.createElement(Highlighted, { text: (_a = result.task.getTitle()) !== null && _a !== void 0 ? _a : '', words: words }), secondary: describe(result) })));
            })),
            total > MAX_RESULTS && (React.createElement(Typography, { color: "text.secondary", sx: { p: 2 } },
                "Showing the first ",
                MAX_RESULTS,
                " of ",
                total,
                " matches. Refine your search to narrow them down."))),
        selected != null && (React.createElement(TaskDetailView, { open: true, closeDetailDialog: () => setSelected(null), slot: toSlot(selected) }))));
};
